import { Button, Card, Form, Grid, Input, InputNumber, Message, Select, Space } from "@arco-design/web-react";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import { EnumSelect, EnumMultiSelect } from "@/components/Enum";
import { TermsDef, TagsDef } from "@/enums/common";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CustomerAPI } from "@/apis/sales";

const FormItem = Form.Item;
const Row = Grid.Row;
const Col = Grid.Col;

export const CustomerAdd = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [accountManagers, setAccountManagers] = useState([]);

    useEffect(() => {
        form.setFieldsValue({ term: 3, contacts: [{}] });
    }, [form]);

    useEffect(() => {
        CustomerAPI.accountManagers().then(list => {
            setAccountManagers(list.map(user => ({
                label: [user.firstName, user.lastName].filter(Boolean).join(" "),
                value: user.id
            })));
        });
    }, []);

    const copyAddressFromBilling = () => {
        const billingAddress = form.getFieldValue("billingAddress");
        const billingCity = form.getFieldValue("billingCity");
        const billingState = form.getFieldValue("billingState");
        const billingZip = form.getFieldValue("billingZip");
        return {
            address: billingAddress,
            city: billingCity,
            state: billingState,
            zip: billingZip
        };
    };

    const onSubmit = async () => {
        try {
            const values = await form.validate();
            setLoading(true);
            const id = await CustomerAPI.add(values);
            Message.success("Customer created successfully");
            navigate(`/workspace/sales/customer/${id}`);
        } catch (error) {
            if (error.name !== 'ValidationError') {
                Message.error(error.message || "Failed to create customer");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card title="Add New Customer" extra={
            <Space>
                <Button type="primary" onClick={onSubmit} loading={loading}>Submit</Button>
                <Button onClick={() => navigate("/workspace/sales/customer")}>Cancel</Button>
            </Space>
        }>
            <Form form={form}
                colon
                labelAlign="left"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
            >
                <Row>
                    <Col span={12}>
                        <FormItem
                            labelCol={{ span: 7 }}
                            wrapperCol={{ span: 17 }}
                            labelAlign="right"
                            label="Customer Name" field="customerName" rules={[{ required: true }]}>
                            <Input />
                        </FormItem>
                    </Col>
                    <Col span={12}>
                        <FormItem
                            labelCol={{ span: 7 }}
                            wrapperCol={{ span: 17 }}
                            label="Account Manager" field="accountManagerId" labelAlign="right">
                            <Select
                                options={accountManagers}
                                placeholder="Select Account Manager"
                                allowClear
                                showSearch
                            />
                        </FormItem>
                    </Col>
                    <Col span={12}>
                        <FormItem
                            labelCol={{ span: 7 }}
                            wrapperCol={{ span: 17 }}
                            label="Term" field="term" labelAlign="right">
                            <EnumSelect enumObj={TermsDef} />
                        </FormItem>
                    </Col>
                    <Col span={12}>
                        <FormItem
                            labelCol={{ span: 7 }}
                            wrapperCol={{ span: 17 }}
                            label="Credit Limit" field="creditLimit" labelAlign="right" rules={[{ required: true }]}>
                            <InputNumber placeholder="Credit Limit" style={{ width: '100%' }} min={0} />
                        </FormItem>
                    </Col>
                </Row>
                <FormItem layout="vertical" label="Main Contact" required>
                    <Form.List field="contacts">
                        {(fields, { add, remove }) => {
                            return (
                                <>
                                    {fields.map((item, index) =>
                                        <Space key={item.key} className="form-item-row">
                                            <FormItem field={item.field + ".id"} hidden>
                                                <Input />
                                            </FormItem>
                                            <FormItem field={item.field + ".contactId"} hidden>
                                                <Input />
                                            </FormItem>
                                            <FormItem field={item.field + ".firstName"} label="First name" noStyle={{ showErrorTip: true }} rules={[{ required: true }]}>
                                                <Input placeholder="First Name" />
                                            </FormItem>
                                            <FormItem field={item.field + ".lastName"} label="Last name" noStyle={{ showErrorTip: true }} rules={[{ required: true }]}>
                                                <Input placeholder="Last Name" />
                                            </FormItem>
                                            <FormItem field={item.field + ".tags"} label="Tag" noStyle={{ showErrorTip: true }} rules={[{ required: true }]}>
                                                <EnumMultiSelect enumObj={TagsDef} placeholder="Tags" style={{ width: 300 }} />
                                            </FormItem>
                                            <FormItem field={item.field + ".email"} label="Email" noStyle={{ showErrorTip: true }} rules={[{ required: true }, { type: "email", message: "Invalid email type" }]}>
                                                <Input placeholder="Email" style={{ width: 280 }} />
                                            </FormItem>
                                            <FormItem field={item.field + ".phone"} label="Phone" noStyle={{ showErrorTip: true }}>
                                                <Input placeholder="Phone" />
                                            </FormItem>
                                            <Button icon={<IconDelete />} type="text" status="danger"
                                                onClick={() => remove(index)}></Button>
                                        </Space>
                                    )}
                                    <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add more...</Button>
                                </>
                            );
                        }}
                    </Form.List>
                </FormItem>
                <FormItem layout="vertical" label="Billing" required>
                    <Space>
                        <FormItem hidden></FormItem>
                        <FormItem field="billingAddress" label="Address" noStyle={{ showErrorTip: true }} rules={[{ required: true }]}>
                            <Input placeholder="Address" style={{ width: 480 }} />
                        </FormItem>
                        <FormItem field="billingCity" label="City" noStyle={{ showErrorTip: true }} rules={[{ required: true }]}>
                            <Input placeholder="City" />
                        </FormItem>
                        <FormItem field="billingState" label="State" noStyle={{ showErrorTip: true }} rules={[{ required: true }]}>
                            <Input placeholder="State" />
                        </FormItem>
                        <FormItem field="billingZip" label="Zip" noStyle={{ showErrorTip: true }} rules={[{ required: true }]}>
                            <Input placeholder="Zip" />
                        </FormItem>
                    </Space>
                </FormItem>
                <FormItem layout="vertical" label="Shipping" required>
                    <Form.List field="shipAddresses">
                        {(fields, { add, remove }) => {
                            return (
                                <>
                                    <Button type="text" onClick={() => {
                                        const address = copyAddressFromBilling();
                                        remove(0);
                                        add(address);
                                    }}>Copy from Billing</Button>
                                    {fields.map((item, index) =>
                                        <Space key={item.key} className="form-item-row">
                                            <FormItem field={item.field + ".id"} hidden><Input /></FormItem>
                                            <FormItem field={item.field + ".contactName"} rules={[{ required: true, message: "Name is required" }]}>
                                                <Input placeholder="Ship to Name" />
                                            </FormItem>
                                            <FormItem field={item.field + ".address"} rules={[{ required: true, message: "Address is required" }]}>
                                                <Input placeholder="Address" style={{ width: 320 }} />
                                            </FormItem>
                                            <FormItem field={item.field + ".city"} rules={[{ required: true, message: "City is required" }]}>
                                                <Input placeholder="City" />
                                            </FormItem>
                                            <FormItem field={item.field + ".state"} rules={[{ required: true, message: "State is required" }]}>
                                                <Input placeholder="State" />
                                            </FormItem>
                                            <FormItem field={item.field + ".zip"} rules={[{ required: true, message: "Zip is required" }]}>
                                                <Input placeholder="Zip" />
                                            </FormItem>
                                            <Button icon={<IconDelete />} type="text" status="danger"
                                                onClick={() => remove(index)}></Button>
                                        </Space>
                                    )}
                                    <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add more...</Button>
                                </>
                            );
                        }}
                    </Form.List>
                </FormItem>
            </Form>
        </Card>
    );
};
